<script lang="ts">
  import {
    generate,
    SIGNAL_LABELS,
    SIGNAL_TYPES,
    type GeneratorParams,
    type SignalType,
    type Tone,
  } from '../dsp/generators';
  import { currentLengthCap } from '../platform/lengthCap';
  import { session } from '../state/session.svelte';

  let { open = $bindable(false) }: { open?: boolean } = $props();

  let dialog: HTMLDialogElement;
  const RATES = [8000, 16000, 22050, 32000, 44100, 48000, 96000];
  const capMinutes = currentLengthCap().minutes;

  let type = $state<SignalType>('sine');
  let sampleRate = $state(48000);
  let duration = $state(5);
  let level = $state(-20);
  let frequency = $state(1000);
  let phase = $state(0);
  let f0 = $state(20);
  let f1 = $state(20000);
  let interval = $state(1);
  let at = $state(0.1);
  let bandLimited = $state(false);
  let seed = $state(1);
  let tones = $state<Tone[]>([
    { frequency: 500, level: -12 },
    { frequency: 5000, level: -12 },
  ]);

  const usesFrequency = $derived(['sine', 'square', 'sawtooth', 'impulse-train'].includes(type));
  const isSweep = $derived(type === 'sweep-lin' || type === 'sweep-log');
  const isNoise = $derived(type === 'white-noise' || type === 'pink-noise');
  const nyquist = $derived(sampleRate / 2);

  const errors = $derived.by(() => {
    const e: string[] = [];
    if (!(duration > 0)) e.push('Duration must be greater than 0.');
    if (duration > capMinutes * 60) e.push(`Duration is limited to ${capMinutes} minutes.`);
    if (usesFrequency && !(frequency > 0)) e.push('Frequency must be greater than 0.');
    if (isSweep && !(f0 > 0 && f1 > 0)) e.push('Sweep frequencies must be greater than 0.');
    if (type === 'click-train' && !(interval > 0)) e.push('Click interval must be greater than 0.');
    if (type === 'multitone' && tones.length === 0) e.push('Add at least one tone.');
    if (!Number.isFinite(level)) e.push('Enter a level in dBFS.');
    return e;
  });

  const warnings = $derived.by(() => {
    const w: string[] = [];
    const fs = [
      ...(usesFrequency ? [frequency] : []),
      ...(isSweep ? [f0, f1] : []),
      ...(type === 'multitone' ? tones.map((t) => t.frequency) : []),
    ];
    if (fs.some((f) => f >= nyquist))
      w.push(`A frequency is at or above Nyquist (${nyquist} Hz): it will alias.`);
    if (level > 0 || (type === 'multitone' && tones.some((t) => t.level > 0)))
      w.push('Levels above 0 dBFS clip in most file formats.');
    if ((type === 'square' || type === 'sawtooth') && !bandLimited)
      w.push(
        'Naive square/sawtooth contain aliased harmonics (tick “band-limited” to reduce them).',
      );
    return w;
  });

  $effect(() => {
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  });

  function params(): GeneratorParams {
    return {
      type,
      sampleRate,
      duration,
      level,
      frequency,
      phase,
      f0,
      f1,
      interval,
      at,
      bandLimited,
      seed,
      tones: $state.snapshot(tones),
    };
  }

  function submit(ev: SubmitEvent) {
    ev.preventDefault();
    if (errors.length) return;
    const p = params();
    const g = generate(p);
    session.open({
      name: g.name,
      sampleRate: g.sampleRate,
      channels: g.channels,
      source: 'generated',
      note: `Generated: ${g.name}, ${p.duration} s at ${p.sampleRate} Hz`,
    });
    open = false;
  }
</script>

<dialog bind:this={dialog} onclose={() => (open = false)} data-testid="generator-dialog">
  <form onsubmit={submit}>
    <h3>Generate a test signal</h3>
    <div class="grid">
      <label>
        Signal
        <select bind:value={type} data-testid="gen-type">
          {#each SIGNAL_TYPES as t (t)}
            <option value={t}>{SIGNAL_LABELS[t]}</option>
          {/each}
        </select>
      </label>
      <label>
        Sample rate
        <select bind:value={sampleRate} data-testid="gen-rate">
          {#each RATES as r (r)}
            <option value={r}>{r} Hz</option>
          {/each}
        </select>
      </label>
      <label>
        Duration (s)
        <input
          type="number"
          min="0.001"
          step="any"
          bind:value={duration}
          data-testid="gen-duration"
        />
      </label>
      {#if type !== 'multitone'}
        <label>
          {isNoise ? 'Level (dBFS, sine-equivalent RMS)' : 'Level (dBFS, peak)'}
          <input type="number" step="any" bind:value={level} data-testid="gen-level" />
        </label>
      {/if}
      {#if usesFrequency}
        <label>
          Frequency (Hz)
          <input
            type="number"
            min="0"
            step="any"
            bind:value={frequency}
            data-testid="gen-frequency"
          />
        </label>
      {/if}
      {#if type === 'sine'}
        <label>
          Phase (°)
          <input type="number" step="any" bind:value={phase} />
        </label>
      {/if}
      {#if type === 'square' || type === 'sawtooth'}
        <label class="check">
          <input type="checkbox" bind:checked={bandLimited} /> Band-limited (PolyBLEP)
        </label>
      {/if}
      {#if isSweep}
        <label>
          Start frequency (Hz)
          <input type="number" min="0" step="any" bind:value={f0} />
        </label>
        <label>
          End frequency (Hz)
          <input type="number" min="0" step="any" bind:value={f1} />
        </label>
      {/if}
      {#if type === 'click-train'}
        <label>
          Interval (s)
          <input type="number" min="0" step="any" bind:value={interval} />
        </label>
      {/if}
      {#if type === 'impulse'}
        <label>
          Position (s)
          <input type="number" min="0" step="any" bind:value={at} />
        </label>
      {/if}
      {#if isNoise}
        <label>
          Random seed
          <input type="number" step="1" bind:value={seed} />
        </label>
      {/if}
    </div>

    {#if type === 'multitone'}
      <fieldset>
        <legend>Tones</legend>
        {#each tones as tone, i (i)}
          <div class="tone">
            <label
              >f (Hz) <input type="number" min="0" step="any" bind:value={tone.frequency} /></label
            >
            <label>level (dBFS) <input type="number" step="any" bind:value={tone.level} /></label>
            <button type="button" onclick={() => tones.splice(i, 1)} aria-label="Remove tone"
              >✕</button
            >
          </div>
        {/each}
        <button type="button" onclick={() => tones.push({ frequency: 1000, level: -20 })}
          >+ Add tone</button
        >
      </fieldset>
    {/if}

    {#each errors as e (e)}<p class="error">{e}</p>{/each}
    {#each warnings as w (w)}<p class="warning">{w}</p>{/each}

    <div class="buttons">
      <button type="button" onclick={() => (open = false)}>Cancel</button>
      <button type="submit" class="primary" disabled={errors.length > 0} data-testid="gen-submit">
        Generate
      </button>
    </div>
  </form>
</dialog>

<style>
  dialog {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--panel);
    color: var(--text);
    padding: 1.25rem 1.5rem;
    max-width: min(560px, 95vw);
    width: 100%;
  }
  dialog::backdrop {
    background: rgb(0 0 0 / 0.35);
  }
  h3 {
    margin-top: 0;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 1rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: 0.88rem;
    color: var(--muted);
  }
  label.check {
    flex-direction: row;
    align-items: center;
    color: var(--text);
  }
  input[type='number'] {
    font: inherit;
    color: var(--text);
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.3rem 0.4rem;
  }
  fieldset {
    margin-top: 1rem;
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .tone {
    display: flex;
    gap: 0.75rem;
    align-items: end;
    margin-bottom: 0.5rem;
  }
  .error {
    color: var(--bad);
    margin: 0.5rem 0 0;
    font-size: 0.88rem;
  }
  .warning {
    color: var(--warn);
    margin: 0.5rem 0 0;
    font-size: 0.88rem;
  }
  .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.25rem;
  }
</style>
